import { useCallback, useEffect, useMemo, useState } from "react";
import {
  acceptAllTagsPending,
  acceptInvitationFromQueue,
  acceptProposedTag,
  acceptSuggestedTag,
  declineInvitation,
  dismissProposedTag,
  dismissSplitSuggestion,
  dismissSuggestedTag,
  downloadDocument,
  fetchDocumentBlob,
  fetchDocumentPreviewHtml,
  listQueueItems,
  resolveEventPending,
  resolveNamePending,
} from "../api";
import {
  ACTION_TYPE_LABELS,
  QUEUE_ACTION_TYPES,
  type EventPendingItem,
  type InvitationPendingItem,
  type NamePendingItem,
  type QueueActionType,
  type QueueItem,
  type SplitCandidateItem,
  type TagsPendingItem,
} from "../types";
import { SplitDocumentModal } from "../../document-manager/components/SplitDocumentModal";
import type { Document } from "../../document-manager/types/api";
import {
  DocumentPreview,
  isViewable,
  type PreviewableDocument,
} from "../../../components/DocumentPreview";
import { ViewButton } from "../../../components/ViewButton";
import { CheckIcon, XIcon } from "../../../components/icons";
import "../../../components/tag-chip.css";
import "./QueueWidget.css";
import { useFeatureFlags } from "../../../context/FeatureFlagsContext";

type SortOrder = "newest" | "oldest" | "by_action";

/** localStorage key for the collapse/expand state. A missing key
 *  means expanded (the default); the value "1" means collapsed.
 *  Bump the suffix if the collapse contract ever changes. */
const COLLAPSED_STORAGE_KEY = "haven:dashboard-queue-collapsed:v1";

/**
 * The Dashboard's human-in-the-loop queue. Backlog-only — items appear
 * because the system needs a decision (or because someone deferred one).
 * Once the user acts, the item disappears; no archive, no confirmation.
 *
 * Adding a new action kind means:
 *   1. Extend the QueueItem union in ../types.ts (+ QUEUE_ACTION_TYPES).
 *   2. Add a fetch/derive branch in listQueueItems().
 *   3. Add a case here in renderItem() AND in the bulk-accept switch.
 * The `never` guards below break the build if step 3 is forgotten.
 */
export function QueueWidget() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const { flags } = useFeatureFlags();
  const { ai_tagging, event_detection, ai_split_suggestions } = flags;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<QueueActionType | "all">("all");
  const [sort, setSort] = useState<SortOrder>("newest");

  // Collapse state — the whole widget body (filters, bulkbar, list)
  // hides so the Dashboard can host multiple stacked widgets over
  // time. Expanded by default; user's choice persists in localStorage
  // so it survives navigation and reload. Bump the version suffix if
  // the collapse contract ever changes (e.g. collapsing a subset).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (collapsed) {
        window.localStorage.setItem(COLLAPSED_STORAGE_KEY, "1");
      } else {
        // Explicit remove so the default (expanded) doesn't need a
        // "0" sentinel — a missing key means expanded.
        window.localStorage.removeItem(COLLAPSED_STORAGE_KEY);
      }
    } catch {
      // Private mode / storage disabled — collapse still works for
      // the current session.
    }
  }, [collapsed]);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Per-item edit buffer for name_pending drafts. Keyed by item.id.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Split-review modal state. Non-null while the user is inspecting a
  // split_candidate's proposed parts in SplitDocumentModal. The queue
  // item itself does NOT vanish on modal open — only on submit
  // (splitDocument flips the doc off pending_review) or on dismiss.
  const [activeSplit, setActiveSplit] = useState<SplitCandidateItem | null>(null);

  // Document-preview state — every queue item exposes a leading View
  // button that opens the shared DocumentPreview modal (same viewer the
  // doc list uses). Non-viewable mime types fall back to a direct
  // download so the reviewer still gets the file for context.
  const [previewDoc, setPreviewDoc] = useState<PreviewableDocument | null>(
    null,
  );

  const downloadForDoc = useCallback(
    async (documentId: string, fallbackName: string) => {
      const { blob, filename } = await downloadDocument(documentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || fallbackName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [],
  );

  const handlePreviewItem = useCallback(
    (item: QueueItem) => {
      // Invitations aren't tied to a document — no preview button on
      // that row shape, so this shouldn't be called. Guard defensively
      // in case a future refactor wires it up accidentally.
      if (item.type === "invitation_pending") return;

      if (isViewable(item.mimeType)) {
        setPreviewDoc({
          id: item.documentId,
          original_name: item.originalName,
          mime_type: item.mimeType,
          size_bytes: item.sizeBytes,
        });
      } else {
        downloadForDoc(item.documentId, item.originalName).catch(() => {
          setError("Could not open the document.");
        });
      }
    },
    [downloadForDoc],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await listQueueItems({ ai_tagging, event_detection, ai_split_suggestions });
      setItems(next);
      // Prune selection + drafts for items that no longer exist.
      const liveIds = new Set(next.map((i) => i.id));
      setSelectedIds((prev) => {
        const kept = new Set<string>();
        prev.forEach((id) => {
          if (liveIds.has(id)) kept.add(id);
        });
        return kept;
      });
      setDrafts((prev) => {
        const merged: Record<string, string> = {};
        for (const it of next) {
          if (it.type === "name_pending") {
            merged[it.id] = prev[it.id] ?? it.suggestedName;
          }
        }
        return merged;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [ai_tagging, event_detection, ai_split_suggestions]);

  useEffect(() => {
    load();
  }, [load]);

  const removeLocally = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setItems((prev) => prev.filter((i) => !idSet.has(i.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      idSet.forEach((id) => next.delete(id));
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      idSet.forEach((id) => delete next[id]);
      return next;
    });
  }, []);

  /**
   * Perform the "accept" action for one item — dispatch on type. Returns
   * void on success, throws on failure. Callers own optimistic removal +
   * error recovery so this can be reused by both single and bulk paths.
   *
   * split_candidate items are NOT accepted here — split is destructive-ish
   * (creates N children, marks parent as split, kicks off N processing
   * workflows), so the user reviews the parts in SplitDocumentModal first.
   * The queue's Review button opens the modal via handleReviewSplit; the
   * bulk-accept path filters split_candidate items out of the target set.
   */
  const acceptItem = useCallback(
    async (item: QueueItem) => {
      switch (item.type) {
        case "name_pending": {
          const draft = drafts[item.id] ?? item.suggestedName;
          await resolveNamePending(item.documentId, "accept", draft);
          return;
        }
        case "tags_pending": {
          await acceptAllTagsPending(item);
          return;
        }
        case "split_candidate": {
          throw new Error(
            "split_candidate items must be reviewed in the modal, not accepted in bulk",
          );
        }
        case "event_pending": {
          // Accept the AI-parsed event as-is. Edits still go through the
          // Documents-page EventPanel (which uses action=edit on the same
          // endpoint). Queue accept is the "yes, that's right" fast path.
          await resolveEventPending(item.documentId, "accept");
          return;
        }
        case "invitation_pending": {
          // Not bulk-acceptable: accepting an invitation switches the
          // user's active org, and doing that 3× in a row from one
          // click is disorienting. Widget's bulk-accept filter drops
          // these before calling acceptItem; this throw is the guard
          // against a future caller who bypasses that filter.
          throw new Error(
            "invitation_pending must be accepted per-item, not in bulk",
          );
        }
        default: {
          const _exhaustive: never = item;
          throw new Error(`Unhandled queue item ${_exhaustive}`);
        }
      }
    },
    [drafts],
  );

  const handleReviewSplit = useCallback((item: SplitCandidateItem) => {
    setActiveSplit(item);
  }, []);

  const handleAcceptInvitation = useCallback(
    async (item: InvitationPendingItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      // Don't remove locally yet — a successful accept switches active
      // org, and the load() at the end refreshes everything anyway.
      try {
        await acceptInvitationFromQueue(item.token);
        // Reload the whole page after accept so the OrganizationProvider
        // picks up the new membership and the org switcher / sidebar
        // reflect the org list change. In-place refresh alone is not
        // enough — the shell's org context is bootstrapped at mount.
        window.location.reload();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to accept invitation");
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [],
  );

  const handleDeclineInvitation = useCallback(
    async (item: InvitationPendingItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      removeLocally([item.id]);
      try {
        await declineInvitation(item.invitationId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to decline invitation");
        await load();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [load, removeLocally],
  );

  const handleDismissSplit = useCallback(
    async (item: SplitCandidateItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      removeLocally([item.id]);
      try {
        await dismissSplitSuggestion(item.documentId);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Dismiss failed");
        await load();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [load, removeLocally],
  );

  /** Fake a minimal Document for the modal from the queue item. The modal
   *  only reads id / mime_type / metadata.pageCount / original_name; the
   *  other Document fields are safe placeholders (the modal never surfaces
   *  them). */
  const docFromSplitItem = useCallback((item: SplitCandidateItem): Document => {
    return {
      id: item.documentId,
      original_name: item.originalName,
      storage_key: "",
      mime_type: item.mimeType,
      size_bytes: 0,
      status: "pending_review",
      ai_suggested_name: null,
      metadata: item.documentMetadata,
      tags: [],
      created_at: item.createdAt,
      updated_at: item.createdAt,
      uploaded_by_name: null,
      uploaded_by_email: null,
    };
  }, []);

  const handleAcceptOne = useCallback(
    async (item: QueueItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      removeLocally([item.id]);
      try {
        await acceptItem(item);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
        await load();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [acceptItem, load, removeLocally],
  );

  const handleRejectName = useCallback(
    async (item: NamePendingItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      removeLocally([item.id]);
      try {
        await resolveNamePending(item.documentId, "reject");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
        await load();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [load, removeLocally],
  );

  const handleRejectEvent = useCallback(
    async (item: EventPendingItem) => {
      setBusyIds((prev) => new Set(prev).add(item.id));
      setError(null);
      removeLocally([item.id]);
      try {
        await resolveEventPending(item.documentId, "reject");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
        await load();
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [load, removeLocally],
  );

  const handleTagChip = useCallback(
    async (
      item: TagsPendingItem,
      scope: "suggested" | "proposed",
      name: string,
      action: "accept" | "dismiss",
    ) => {
      const chipKey = `${item.id}::${scope}::${name}::${action}`;
      setBusyIds((prev) => new Set(prev).add(chipKey));
      setError(null);
      try {
        if (scope === "suggested") {
          if (action === "accept") await acceptSuggestedTag(item.documentId, name);
          else await dismissSuggestedTag(item.documentId, name);
        } else {
          if (action === "accept") await acceptProposedTag(item.documentId, name);
          else await dismissProposedTag(item.documentId, name);
        }
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed");
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(chipKey);
          return next;
        });
      }
    },
    [load],
  );

  const handleBulkAccept = useCallback(async () => {
    if (selectedIds.size === 0 || bulkRunning) return;
    // split_candidate + invitation_pending items are never bulk-
    // acceptable — split needs the modal review, invitations switch
    // active org (disorienting in bulk). Filter them out defensively
    // even though the per-row UI already refuses to select them.
    const targets = items.filter(
      (i) =>
        selectedIds.has(i.id) &&
        i.type !== "split_candidate" &&
        i.type !== "invitation_pending",
    );
    if (targets.length === 0) return;

    setBulkRunning(true);
    setError(null);
    // Optimistically drop all targets — failures come back via a refetch.
    removeLocally(targets.map((i) => i.id));

    const failures: string[] = [];
    for (const item of targets) {
      try {
        await acceptItem(item);
      } catch (err) {
        failures.push(err instanceof Error ? err.message : "Action failed");
      }
    }

    if (failures.length > 0) {
      setError(
        failures.length === 1
          ? failures[0]
          : `${failures.length} items failed. ${failures[0]}`,
      );
      await load();
    }
    setBulkRunning(false);
  }, [acceptItem, bulkRunning, items, load, removeLocally, selectedIds]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visible = useMemo(() => {
    const filtered =
      filter === "all" ? items : items.filter((i) => i.type === filter);
    const sorted = [...filtered];
    if (sort === "newest") {
      sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    } else if (sort === "oldest") {
      sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    } else {
      sorted.sort(
        (a, b) => a.type.localeCompare(b.type) || b.createdAt.localeCompare(a.createdAt),
      );
    }
    return sorted;
  }, [items, filter, sort]);

  // Select-all only touches bulk-eligible rows (excludes split_candidate
  // and invitation_pending — same rationale as the bulk-accept filter).
  // Otherwise clicking Select-all would silently include rows the bulk
  // handler then drops on the floor, and the "N selected" count would
  // lie about how many the button actually processes.
  const bulkEligible = useMemo(
    () =>
      visible.filter(
        (i) => i.type !== "split_candidate" && i.type !== "invitation_pending",
      ),
    [visible],
  );
  const allVisibleSelected =
    bulkEligible.length > 0 &&
    bulkEligible.every((i) => selectedIds.has(i.id));
  const someVisibleSelected =
    !allVisibleSelected && bulkEligible.some((i) => selectedIds.has(i.id));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        bulkEligible.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      bulkEligible.forEach((i) => next.add(i.id));
      return next;
    });
  }, [allVisibleSelected, bulkEligible]);

  return (
    <section
      className={`queue-widget ${collapsed ? "queue-widget--collapsed" : ""}`}
      aria-label="Queue"
    >
      <header className="queue-widget__header">
        {/* Title doubles as the collapse toggle so the whole heading
            row (minus the filter/sort controls on the right) is a
            single click target. Chevron rotates to signal state. */}
        <button
          type="button"
          className="queue-widget__toggle"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          aria-controls="queue-widget-body"
          title={collapsed ? "Expand queue" : "Collapse queue"}
        >
          <span
            className={`queue-widget__caret ${collapsed ? "queue-widget__caret--collapsed" : ""}`}
            aria-hidden="true"
          >
            ▾
          </span>
          <h3 className="queue-widget__title">
            Queue
            {items.length > 0 && (
              <span className="queue-widget__count" aria-label={`${items.length} items`}>
                {items.length}
              </span>
            )}
          </h3>
        </button>
        {/* Filter / sort controls disappear when collapsed — they only
            change what the list shows, and there's no list to change
            while the body is hidden. Reappear on expand. */}
        {!collapsed && (
          <div className="queue-widget__controls">
          <label>
            <span className="visually-hidden">Filter by action type</span>
            <select
              className="queue-widget__select"
              value={filter}
              onChange={(e) =>
                setFilter(e.target.value as QueueActionType | "all")
              }
              aria-label="Filter queue by action type"
            >
              <option value="all">All actions</option>
              {QUEUE_ACTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ACTION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="visually-hidden">Sort order</span>
            <select
              className="queue-widget__select"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortOrder)}
              aria-label="Sort queue"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="by_action">By action type</option>
            </select>
          </label>
          </div>
        )}
      </header>

      {!collapsed && visible.length > 0 && !loading && !error && (
        <div className="queue-widget__bulkbar" role="toolbar" aria-label="Bulk actions">
          <label className="queue-widget__selectall">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = someVisibleSelected;
              }}
              onChange={toggleSelectAll}
              aria-label={allVisibleSelected ? "Deselect all" : "Select all"}
            />
            <span>
              {allVisibleSelected
                ? "All selected"
                : someVisibleSelected
                  ? `${selectedIds.size} selected`
                  : "Select all"}
            </span>
          </label>
          <button
            type="button"
            className="queue-widget__bulk-accept"
            disabled={selectedIds.size === 0 || bulkRunning}
            onClick={handleBulkAccept}
            aria-label={`Accept ${selectedIds.size} selected items`}
          >
            {bulkRunning
              ? "Accepting…"
              : `Accept selected${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
          </button>
        </div>
      )}

      <div
        id="queue-widget-body"
        className="queue-widget__body"
        hidden={collapsed}
      >
        {loading ? (
          <div className="queue-widget__loading">Loading…</div>
        ) : error ? (
          <div className="queue-widget__error" role="alert">
            {error}
          </div>
        ) : visible.length === 0 ? (
          <div className="queue-widget__empty">Nothing waiting.</div>
        ) : (
          <ul className="queue-widget__list">
            {visible.map((item) => (
              <li key={item.id}>
                {renderItem(item, {
                  busy: busyIds.has(item.id),
                  selected: selectedIds.has(item.id),
                  onToggleSelected: () => toggleSelected(item.id),
                  draft: drafts[item.id] ?? "",
                  setDraft: (v) => setDrafts((p) => ({ ...p, [item.id]: v })),
                  onAccept: handleAcceptOne,
                  onRejectName: handleRejectName,
                  onTagChip: handleTagChip,
                  chipBusy: (chipKey: string) => busyIds.has(chipKey),
                  onReviewSplit: handleReviewSplit,
                  onDismissSplit: handleDismissSplit,
                  onRejectEvent: handleRejectEvent,
                  onPreview: handlePreviewItem,
                  onAcceptInvitation: handleAcceptInvitation,
                  onDeclineInvitation: handleDeclineInvitation,
                })}
              </li>
            ))}
          </ul>
        )}
      </div>

      {activeSplit && (
        <SplitDocumentModal
          doc={docFromSplitItem(activeSplit)}
          initialPageCount={activeSplit.pageCount}
          initialParts={activeSplit.proposedParts}
          onClose={() => setActiveSplit(null)}
          onSuccess={() => {
            // splitDocument flips the parent doc off pending_review, so
            // its split_suggestion is no longer reachable from our
            // ?status=pending_review query. Refetch to drop the row.
            setActiveSplit(null);
            void load();
          }}
        />
      )}

      {previewDoc && (
        <DocumentPreview
          doc={previewDoc}
          fetchBlob={fetchDocumentBlob}
          fetchPreviewHtml={fetchDocumentPreviewHtml}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => {
            downloadForDoc(previewDoc.id, previewDoc.original_name).catch(() => {
              setError("Could not download the document.");
            });
          }}
        />
      )}
    </section>
  );
}

interface ItemRenderContext {
  busy: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  draft: string;
  setDraft: (v: string) => void;
  onAccept: (item: QueueItem) => void;
  onRejectName: (item: NamePendingItem) => void;
  onTagChip: (
    item: TagsPendingItem,
    scope: "suggested" | "proposed",
    name: string,
    action: "accept" | "dismiss",
  ) => void;
  chipBusy: (chipKey: string) => boolean;
  onReviewSplit: (item: SplitCandidateItem) => void;
  onDismissSplit: (item: SplitCandidateItem) => void;
  onRejectEvent: (item: EventPendingItem) => void;
  /** Open the shared preview modal (or fall back to a direct download
   *  for non-viewable mime types). Icon-only leading button on every
   *  row — reviewers can look at the file for context without
   *  leaving the queue. */
  onPreview: (item: QueueItem) => void;
  onAcceptInvitation: (item: InvitationPendingItem) => void;
  onDeclineInvitation: (item: InvitationPendingItem) => void;
}

function renderItem(item: QueueItem, ctx: ItemRenderContext) {
  switch (item.type) {
    case "name_pending":
      return <NamePendingRow item={item} ctx={ctx} />;
    case "tags_pending":
      return <TagsPendingRow item={item} ctx={ctx} />;
    case "split_candidate":
      return <SplitCandidateRow item={item} ctx={ctx} />;
    case "event_pending":
      return <EventPendingRow item={item} ctx={ctx} />;
    case "invitation_pending":
      return <InvitationPendingRow item={item} ctx={ctx} />;
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

function SelectCheckbox({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <label className="queue-item__select">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
      />
    </label>
  );
}

// CheckIcon / XIcon moved to apps/shell/src/components/icons.tsx so the
// Upload Inbox tag chips can share the same SVG. Imported at the top of
// this file.

function NamePendingRow({
  item,
  ctx,
}: {
  item: NamePendingItem;
  ctx: ItemRenderContext;
}) {
  const { busy, selected, onToggleSelected, draft, setDraft, onAccept, onRejectName, onPreview } = ctx;
  return (
    <article className={`queue-item ${selected ? "queue-item--selected" : ""}`}>
      <SelectCheckbox
        checked={selected}
        onChange={onToggleSelected}
        disabled={busy}
        label={`Select item for ${item.originalName}`}
      />
      <span className="queue-item__badge">Confirm name</span>
      <div className="queue-item__body">
        <input
          className="queue-item__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onAccept(item);
          }}
          disabled={busy}
          aria-label={`Filename for ${item.originalName}`}
        />
        <span className="queue-item__original">
          Original: {item.originalName}
        </span>
        <span className="queue-item__meta">
          Uploaded {new Date(item.createdAt).toLocaleString()}
        </span>
      </div>
      <ViewButton
        onClick={() => onPreview(item)}
        documentName={item.originalName}
        disabled={busy}
      />
      <div className="queue-item__actions">
        <button
          type="button"
          className="queue-item__btn queue-item__btn--accept"
          disabled={busy}
          onClick={() => onAccept(item)}
          aria-label={`Accept name for ${item.originalName}`}
        >
          <CheckIcon size={12} />
          Accept
        </button>
        <button
          type="button"
          className="queue-item__btn queue-item__btn--reject"
          disabled={busy}
          onClick={() => onRejectName(item)}
          aria-label={`Keep original name for ${item.originalName}`}
        >
          Keep original
        </button>
      </div>
    </article>
  );
}

function TagsPendingRow({
  item,
  ctx,
}: {
  item: TagsPendingItem;
  ctx: ItemRenderContext;
}) {
  const { busy, selected, onToggleSelected, onAccept, onTagChip, chipBusy, onPreview } = ctx;
  const totalChips = item.suggestedTags.length + item.proposedTags.length;

  return (
    <article className={`queue-item ${selected ? "queue-item--selected" : ""}`}>
      <SelectCheckbox
        checked={selected}
        onChange={onToggleSelected}
        disabled={busy}
        label={`Select tag decisions for ${item.originalName}`}
      />
      <span className="queue-item__badge queue-item__badge--tags">Confirm tags</span>
      <div className="queue-item__body">
        <div className="queue-item__doc-name" title={item.originalName}>
          {item.originalName}
        </div>
        <div
          className="queue-item__chips"
          title={item.suggestionReasoning ?? undefined}
        >
          {item.suggestedTags.map((name) => {
            const acceptKey = `${item.id}::suggested::${name}::accept`;
            const dismissKey = `${item.id}::suggested::${name}::dismiss`;
            const chipDisabled =
              busy || chipBusy(acceptKey) || chipBusy(dismissKey);
            return (
              <span key={`s-${name}`} className="tag-chip tag-chip--suggested">
                {name}
                <button
                  type="button"
                  className="tag-chip__btn tag-chip__btn--accept"
                  disabled={chipDisabled}
                  onClick={() => onTagChip(item, "suggested", name, "accept")}
                  aria-label={`Accept tag ${name}`}
                  title={`Accept tag ${name}`}
                >
                  <CheckIcon />
                </button>
                <button
                  type="button"
                  className="tag-chip__btn tag-chip__btn--dismiss"
                  disabled={chipDisabled}
                  onClick={() => onTagChip(item, "suggested", name, "dismiss")}
                  aria-label={`Dismiss tag ${name}`}
                  title={`Dismiss tag ${name}`}
                >
                  <XIcon />
                </button>
              </span>
            );
          })}
          {item.proposedTags.map((name) => {
            const acceptKey = `${item.id}::proposed::${name}::accept`;
            const dismissKey = `${item.id}::proposed::${name}::dismiss`;
            const chipDisabled =
              busy || chipBusy(acceptKey) || chipBusy(dismissKey);
            return (
              <span key={`p-${name}`} className="tag-chip tag-chip--proposed">
                <span className="tag-chip__prefix">NEW:</span>
                {name}
                <button
                  type="button"
                  className="tag-chip__btn tag-chip__btn--accept"
                  disabled={chipDisabled}
                  onClick={() => onTagChip(item, "proposed", name, "accept")}
                  aria-label={`Create and apply tag ${name}`}
                  title={`Create and apply tag ${name}`}
                >
                  <CheckIcon />
                </button>
                <button
                  type="button"
                  className="tag-chip__btn tag-chip__btn--dismiss"
                  disabled={chipDisabled}
                  onClick={() => onTagChip(item, "proposed", name, "dismiss")}
                  aria-label={`Dismiss proposed tag ${name}`}
                  title={`Dismiss proposed tag ${name}`}
                >
                  <XIcon />
                </button>
              </span>
            );
          })}
        </div>
        <span className="queue-item__meta">
          Uploaded {new Date(item.createdAt).toLocaleString()}
        </span>
      </div>
      <ViewButton
        onClick={() => onPreview(item)}
        documentName={item.originalName}
        disabled={busy}
      />
      <div className="queue-item__actions">
        <button
          type="button"
          className="queue-item__btn queue-item__btn--accept"
          disabled={busy || totalChips === 0}
          onClick={() => onAccept(item)}
          aria-label={`Accept all tag suggestions for ${item.originalName}`}
        >
          <CheckIcon size={12} />
          Accept all
        </button>
      </div>
    </article>
  );
}

function SplitCandidateRow({
  item,
  ctx,
}: {
  item: SplitCandidateItem;
  ctx: ItemRenderContext;
}) {
  const { busy, onReviewSplit, onDismissSplit, onPreview } = ctx;
  // No SelectCheckbox — split_candidate items don't participate in bulk
  // accept (destructive action, must go through the modal). An empty
  // grid cell keeps the row aligned with checkboxed rows above/below.
  return (
    <article className="queue-item queue-item--noselect">
      {/* Empty leading cell keeps this row aligned with checkboxed rows;
          split_candidate items don't participate in bulk accept. */}
      <span aria-hidden="true" />
      <span className="queue-item__badge queue-item__badge--split">Review split</span>
      <div className="queue-item__body">
        <div className="queue-item__doc-name" title={item.originalName}>
          {item.originalName}
        </div>
        <div className="queue-item__original">
          {item.proposedParts.length} parts across {item.pageCount} pages
          {" · "}
          <span
            className="queue-item__confidence"
            title={item.reasoning ?? undefined}
          >
            {item.confidence} confidence
          </span>
        </div>
        {item.reasoning && (
          <div className="queue-item__reasoning">{item.reasoning}</div>
        )}
        <span className="queue-item__meta">
          Uploaded {new Date(item.createdAt).toLocaleString()}
        </span>
      </div>
      <ViewButton
        onClick={() => onPreview(item)}
        documentName={item.originalName}
        disabled={busy}
      />
      <div className="queue-item__actions">
        <button
          type="button"
          className="queue-item__btn queue-item__btn--accept"
          disabled={busy}
          onClick={() => onReviewSplit(item)}
          aria-label={`Review proposed split for ${item.originalName}`}
        >
          Review
        </button>
        <button
          type="button"
          className="queue-item__btn queue-item__btn--reject"
          disabled={busy}
          onClick={() => onDismissSplit(item)}
          aria-label={`Dismiss split suggestion for ${item.originalName}`}
        >
          Dismiss
        </button>
      </div>
    </article>
  );
}

function EventPendingRow({
  item,
  ctx,
}: {
  item: EventPendingItem;
  ctx: ItemRenderContext;
}) {
  const { busy, selected, onToggleSelected, onAccept, onRejectEvent, onPreview } = ctx;
  return (
    <article className={`queue-item ${selected ? "queue-item--selected" : ""}`}>
      <SelectCheckbox
        checked={selected}
        onChange={onToggleSelected}
        disabled={busy}
        label={`Select event for ${item.originalName}`}
      />
      <span className="queue-item__badge queue-item__badge--event">Confirm event</span>
      <div className="queue-item__body">
        <div className="queue-item__doc-name" title={item.title}>
          {item.title}
        </div>
        <div className="queue-item__event-when">{formatEventWhen(item)}</div>
        {item.location && (
          <div className="queue-item__event-location" title={item.location}>
            📍 {item.location}
          </div>
        )}
        <span className="queue-item__original">
          From: {item.originalName}
        </span>
        <span className="queue-item__meta">
          Detected {new Date(item.createdAt).toLocaleString()}
        </span>
      </div>
      <ViewButton
        onClick={() => onPreview(item)}
        documentName={item.originalName}
        disabled={busy}
      />
      <div className="queue-item__actions">
        <button
          type="button"
          className="queue-item__btn queue-item__btn--accept"
          disabled={busy}
          onClick={() => onAccept(item)}
          aria-label={`Accept event "${item.title}"`}
        >
          <CheckIcon size={12} />
          Accept
        </button>
        <button
          type="button"
          className="queue-item__btn queue-item__btn--reject"
          disabled={busy}
          onClick={() => onRejectEvent(item)}
          aria-label={`Reject event "${item.title}"`}
        >
          Reject
        </button>
      </div>
    </article>
  );
}

/** Format the event's timing for the row's "when" line. Handles the four
 *  cases (all-day-single-day, all-day-span, timed-single, timed-span)
 *  without the row having to deal with locale/timezone details itself. */
function formatEventWhen(item: EventPendingItem): string {
  const start = new Date(item.startTime);
  const end = item.endTime ? new Date(item.endTime) : null;

  if (item.allDay) {
    const startDay = start.toLocaleDateString();
    if (!end) return startDay;
    const endDay = end.toLocaleDateString();
    return startDay === endDay ? startDay : `${startDay} – ${endDay}`;
  }

  const startFmt = start.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  if (!end) return startFmt;
  const sameDay = start.toDateString() === end.toDateString();
  const endFmt = sameDay
    ? end.toLocaleTimeString(undefined, { timeStyle: "short" })
    : end.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
  return `${startFmt} – ${endFmt}`;
}

function InvitationPendingRow({
  item,
  ctx,
}: {
  item: InvitationPendingItem;
  ctx: ItemRenderContext;
}) {
  const { busy, onAcceptInvitation, onDeclineInvitation } = ctx;
  // Deliberately no SelectCheckbox — invitations aren't bulk-acceptable
  // (accept switches active org). An empty grid cell in slot 1 keeps
  // the row's grid columns aligned with checkboxed rows above/below.
  return (
    <article className="queue-item queue-item--noselect">
      <span aria-hidden="true" />
      <span className="queue-item__badge queue-item__badge--invitation">
        Accept invitation
      </span>
      <div className="queue-item__body">
        <div className="queue-item__doc-name">
          Join <strong>{item.orgName}</strong>
        </div>
        <div className="queue-item__original">
          Invited by <strong>{item.invitedByName}</strong> as{" "}
          {item.role.toLowerCase()}
        </div>
        <span className="queue-item__meta">
          Sent to {item.email} · expires{" "}
          {new Date(item.expiresAt).toLocaleDateString()}
        </span>
      </div>
      <div className="queue-item__actions">
        <button
          type="button"
          className="queue-item__btn queue-item__btn--accept"
          disabled={busy}
          onClick={() => onAcceptInvitation(item)}
          aria-label={`Accept invitation to ${item.orgName}`}
        >
          <CheckIcon size={12} />
          Accept
        </button>
        <button
          type="button"
          className="queue-item__btn queue-item__btn--reject"
          disabled={busy}
          onClick={() => onDeclineInvitation(item)}
          aria-label={`Decline invitation to ${item.orgName}`}
        >
          Decline
        </button>
      </div>
    </article>
  );
}
